import { useEffect, useRef, useCallback } from 'react';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { useQuery } from '@apollo/client';
import { GET_EXOLORE_COLLECTIONS } from '@/graphql/queries';
import { AWS_URL } from '@/constants';
import { defaultImage } from '@/utility/productDetailHelpers';
import { useRouter } from 'next/navigation';
import { Swiper, SwiperSlide } from 'swiper/react';
import { FreeMode } from 'swiper/modules';
import 'swiper/css';
import 'swiper/css/free-mode';

gsap.registerPlugin(ScrollTrigger);

export default function JewelleryCategoriesGrid() {
  const containerRef = useRef(null);
  const titleRef = useRef(null);
  const categoryRefs = useRef([]);

  const { data, loading, error } = useQuery(GET_EXOLORE_COLLECTIONS, {

      variables: { where: { type: { _eq: "EXPLORE_COLLECTION" } } },
    fetchPolicy: "network-only",

  });
  const router = useRouter();

  const getImageUrl = useCallback((file) => {
    if (file?.path) {
      return AWS_URL ? `${AWS_URL}${file.path}` : defaultImage;
    }
    return defaultImage;
  }, []);

  const categories =
    data?.tenant_org_web_configuration?.[0]?.web_configuration_meta?.map(
      (item) => ({
        title: item.title,
        image: getImageUrl(item.file),
        redirectionUrl: item.redirection_url,
        priority: item.priority,
      })
    ) || [];

  const handleCategoryClick = (category) => {
    if (category.redirectionUrl) {
      router.push(category.redirectionUrl);
    }
  };

  // Split categories into 2 rows for mobile swiper
  const row1 = categories.filter((_, i) => i % 2 === 0);
  const row2 = categories.filter((_, i) => i % 2 !== 0);

  useEffect(() => {
    if (categories.length === 0) return;

    const ctx = gsap.context(() => {
      gsap.from(titleRef.current, {
        y: 50,
        opacity: 0,
        duration: 1,
        ease: 'power4.out',
      });

      const desktopCards = categoryRefs.current.filter(Boolean);
      gsap.from(desktopCards, {
        scrollTrigger: {
          trigger: containerRef.current,
          start: 'top 80%',
        },
        scale: 0.8,
        opacity: 0,
        duration: 0.6,
        stagger: 0.05,
        ease: 'back.out(1.2)',
      });
    }, containerRef);

    return () => ctx.revert();
  }, [categories]);

  const CategoryCard = ({ category, size = 'md' }) => (
    <div
      className="cursor-pointer group"
      onClick={() => handleCategoryClick(category)}
    >
      <div
        className={`relative aspect-square bg-gradient-to-br rounded-3xl overflow-hidden shadow-md hover:shadow-xl transition-all duration-300 ${
          size === 'sm' ? 'mb-2' : 'mb-3'
        }`}
      >
        <div className="absolute inset-0 backdrop-blur-sm" />
        <div
          className={`absolute inset-0 flex items-center justify-center ${
            size === 'sm' ? 'p-4' : 'p-6'
          }`}
        >
          <img
            src={category.image}
            alt={category.title}
            className="w-full h-full object-contain group-hover:scale-110 transition-transform duration-500"
            onError={(e) => {
              e.target.src = defaultImage;
            }}
          />
        </div>
        <div className="absolute inset-0 bg-gradient-to-t from-amber-600/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
      </div>
      <p
        className={`text-center text-gray-600 font-medium group-hover:text-amber-600 transition-colors duration-300 leading-tight px-1 ${
          size === 'sm' ? 'text-xs' : 'text-sm md:text-base'
        }`}
      >
        {category.title}
      </p>
    </div>
  );

  const swiperProps = {
    modules: [FreeMode],
    freeMode: true,
    slidesPerView: 'auto',
    spaceBetween: 12,
    grabCursor: true,
    className: 'pb-2',
  };

  if (loading) {
    return (
      <div className="bg-gradient-to-br from-amber-50 via-white to-rose-50 py-16">
        <div className="container mx-auto px-4">
          <h2 className="text-4xl md:text-5xl font-serif text-center text-gray-900 mb-12">
            Explore Our <span className="text-amber-600 italic">Collections</span>
          </h2>
          {/* Mobile skeleton */}
          <div className="block md:hidden">
            {[0, 1].map((row) => (
              <div key={row} className="flex gap-3 overflow-hidden mb-3">
                {[...Array(5)].map((_, i) => (
                  <div key={i} className="flex-shrink-0 w-28">
                    <div className="aspect-square bg-gradient-to-br from-amber-50 to-rose-50 rounded-3xl shadow-md mb-2 animate-pulse" />
                    <div className="h-3 bg-gray-200 rounded animate-pulse mx-2" />
                  </div>
                ))}
              </div>
            ))}
          </div>
          {/* Desktop skeleton */}
          <div className="hidden md:grid grid-cols-4 lg:grid-cols-8 gap-4 md:gap-6">
            {[...Array(16)].map((_, index) => (
              <div key={index}>
                <div className="relative aspect-square bg-gradient-to-br from-amber-50 to-rose-50 rounded-3xl overflow-hidden shadow-md mb-3 animate-pulse" />
                <div className="h-4 bg-gray-200 rounded animate-pulse" />
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-gradient-to-br from-amber-50 via-white to-rose-50 py-16 flex items-center justify-center">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-4xl md:text-5xl font-serif text-gray-900 mb-4">
            Explore Our <span className="text-amber-600 italic">Collections</span>
          </h2>
          <p className="text-gray-600">Unable to load categories. Please try again later.</p>
        </div>
      </div>
    );
  }

  if (categories.length === 0) {
    return (
      <div className="bg-gradient-to-br from-amber-50 via-white to-rose-50 py-16 flex items-center justify-center">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-4xl md:text-5xl font-serif text-gray-900 mb-4">
            Explore Our <span className="text-amber-600 italic">Collections</span>
          </h2>
          <p className="text-gray-600">No categories available at the moment.</p>
        </div>
      </div>
    );
  }

  return (
    <div ref={containerRef} className="bg-gradient-to-br py-16">
      <div className="container mx-auto px-4">
        <h2
          ref={titleRef}
          className="text-4xl md:text-5xl font-serif text-center text-gray-900 mb-12"
        >
          Explore Our <span className="text-amber-600 italic">Collections</span>
        </h2>

        {/* ── MOBILE: 2-row Swiper ── */}
        <div className="block md:hidden space-y-3">
          <Swiper {...swiperProps}>
            {row1.map((category, i) => (
              <SwiperSlide key={i} style={{ width: '7rem' }}>
                <CategoryCard category={category} size="sm" />
              </SwiperSlide>
            ))}
          </Swiper>
          <Swiper {...swiperProps}>
            {row2.map((category, i) => (
              <SwiperSlide key={i} style={{ width: '7rem' }}>
                <CategoryCard category={category} size="sm" />
              </SwiperSlide>
            ))}
          </Swiper>
        </div>

        {/* ── DESKTOP: original grid ── */}
        <div className="hidden md:grid grid-cols-4 lg:grid-cols-8 gap-4 md:gap-6">
          {categories.map((category, index) => (
            <div
              key={index}
              ref={(el) => (categoryRefs.current[index] = el)}
              className="group cursor-pointer"
              onClick={() => handleCategoryClick(category)}
            >
              <div className="relative aspect-square bg-gradient-to-br from-amber-50 to-rose-50 rounded-3xl overflow-hidden shadow-md hover:shadow-xl transition-all duration-300 mb-3">
                <div className="absolute inset-0 bg-white/80 backdrop-blur-sm" />
                <div className="absolute inset-0 flex items-center justify-center p-6">
                  <img
                    src={category.image}
                    alt={category.title}
                    className="w-full h-full object-contain group-hover:scale-110 transition-transform duration-500"
                    onError={(e) => {
                      e.target.src = defaultImage;
                    }}
                  />
                </div>
                <div className="absolute inset-0 bg-gradient-to-t from-amber-600/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
              </div>
              <p className="text-center text-sm md:text-base text-gray-600 font-medium group-hover:text-amber-600 transition-colors duration-300">
                {category.title}
              </p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}